// Owns the mesh-generation worker and all traffic to it.
//
// Mesh requests keep their original coalescing semantics: one in flight at a
// time, changes made meanwhile collapse into a single follow-up request with a
// sticky refit latch. STL and zip requests (used by the cart) share the same
// single-in-flight discipline via a small promise queue, so a panic during an
// STL build replaces the worker just like a mesh panic does, and the common
// param-tweak path keeps its low latency.

export type MeshResult = {
  vertices: Float32Array;
  ms: number;
  refit: boolean;
  // True when a coalesced mesh rebuild is queued to run next, so the caller
  // should keep the spinner up rather than hide it (it'll be re-shown for the
  // follow-up anyway). Hide only when the burst is going idle.
  moreQueued: boolean;
};

// One printable STL file produced from a scene.
export type SceneFile = { filename: string; bytes: Uint8Array };

// One cart item of a 3MF build; label names the item in errors.
export type ThreeMfItem = {
  scene: string;
  mode: string;
  clearance: number;
  quantity: number;
  label: string;
};

// A queued STL/zip/3MF job, resolved when its reply arrives.
type Job =
  | {
      kind: "stls";
      requestId: number;
      request: { scene: string; mode: string; clearance: number };
      resolve: (files: SceneFile[]) => void;
      reject: (err: Error) => void;
    }
  | {
      kind: "zip";
      requestId: number;
      request: { filenames: string[]; stls: Uint8Array[]; bomText: string };
      resolve: (zip: Uint8Array) => void;
      reject: (err: Error) => void;
    }
  | {
      kind: "3mf";
      requestId: number;
      request: { items: ThreeMfItem[] };
      resolve: (bytes: Uint8Array) => void;
      reject: (err: Error) => void;
    };

export type WorkerClientCallbacks = {
  // A freshly built mesh is ready to upload.
  onMesh: (result: MeshResult) => void;
  // Mesh generation crashed (and the worker was replaced); no rebuild queued.
  onMeshError: (message: string) => void;
  // A mesh request is being sent (show the spinner grace period).
  onMeshRequested: () => void;
};

export class WorkerClient {
  private worker!: Worker;
  private workerReady = false;
  private cb: WorkerClientCallbacks;

  // How to (re)build the scene JSON for the coalesced follow-up path.
  private sceneJson: () => string;

  // Mesh coalescing state.
  private meshInflight = false;
  private dirty = false;
  // Whether the coalesced follow-up should reframe the camera. Sticky: once a
  // refit (first load / archetype switch) is queued it stays queued even if
  // later parameter tweaks coalesce onto it.
  private dirtyRefit = false;

  // STL/zip job queue. At most one job is in flight (tracked by activeJob);
  // mesh requests and jobs never run simultaneously.
  private jobs: Job[] = [];
  private activeJob: Job | null = null;

  private requestId = 0;

  constructor(sceneJson: () => string, cb: WorkerClientCallbacks) {
    this.sceneJson = sceneJson;
    this.cb = cb;
    this.startWorker();
  }

  private startWorker() {
    this.workerReady = false;
    // Both bundles sit in dist/, so plain runtime URL resolution finds
    // worker.js without any esbuild rewriting.
    this.worker = new Worker(new URL("./worker.js", import.meta.url), {
      type: "module",
    });
    this.worker.onmessage = (e) => this.onMessage(e);
    // Errors the worker couldn't report itself (e.g. it failed to even load).
    this.worker.onerror = (e) => {
      e.preventDefault();
      this.onWorkerError(e.message || "worker failed");
    };
  }

  private onMessage(e: MessageEvent) {
    const data = e.data;
    if (data.type === "ready") {
      this.workerReady = true;
      this.resume();
      return;
    }
    if (data.type === "error") {
      this.onWorkerError(data.message);
      return;
    }
    if (data.type === "mesh") {
      this.meshInflight = false;
      this.cb.onMesh({
        vertices: data.vertices,
        ms: data.ms,
        refit: data.refit,
        moreQueued: this.dirty,
      });
      this.resume();
      return;
    }
    if (data.type === "stls" || data.type === "zip" || data.type === "3mf") {
      const job = this.activeJob;
      this.activeJob = null;
      if (job) {
        if (data.type === "stls" && job.kind === "stls") job.resolve(data.files);
        else if (data.type === "zip" && job.kind === "zip") job.resolve(data.zip);
        else if (data.type === "3mf" && job.kind === "3mf") {
          // A 3MF reply can carry a validation failure (an item with nothing to
          // print); the worker is healthy, so only this job fails.
          if (data.error !== undefined) job.reject(new Error(data.error));
          else job.resolve(data.bytes);
        }
      }
      // A param tweak made during the job may have queued a coalesced mesh
      // rebuild; prefer that, else run the next job.
      this.resume();
      return;
    }
  }

  // Resume queued work after the worker went idle: a coalesced mesh follow-up
  // takes priority over pending STL/zip jobs.
  private resume() {
    if (this.dirty) {
      this.dirty = false;
      this.requestMesh(this.dirtyRefit);
    } else {
      this.pump();
    }
  }

  // A crashed wasm instance can't be reused, so replace the whole worker. Any
  // queued mesh change is retried; a queued STL/zip job (and the active one) is
  // rejected so the cart UI surfaces the error and stays unchanged.
  private onWorkerError(message: string) {
    // Whether the crash concerns a mesh build: a mesh was in flight, or a
    // coalesced mesh rebuild is queued. A job-only crash (STL/zip with no dirty
    // mesh) must not blank the last-good mesh status; it surfaces solely through
    // the job's rejection (the cart shows its own overlay error).
    const meshAffected = this.meshInflight || this.dirty;
    this.meshInflight = false;
    this.worker.terminate();

    const err = new Error(message);
    const active = this.activeJob;
    const queued = this.jobs;
    this.activeJob = null;
    this.jobs = [];

    this.startWorker();

    if (active) active.reject(err);
    for (const job of queued) job.reject(err);

    if (this.dirty) {
      this.dirty = false;
      this.requestMesh(this.dirtyRefit);
    } else if (meshAffected) {
      this.cb.onMeshError(message);
    }
  }

  // Start the next STL/zip job if nothing is in flight.
  private pump() {
    if (!this.workerReady) return;
    if (this.meshInflight || this.activeJob) return;
    const job = this.jobs.shift();
    if (!job) return;
    this.activeJob = job;
    if (job.kind === "stls") {
      this.worker.postMessage({
        type: "stls",
        requestId: job.requestId,
        scene: job.request.scene,
        mode: job.request.mode,
        clearance: job.request.clearance,
      });
    } else if (job.kind === "3mf") {
      this.worker.postMessage({
        type: "3mf",
        requestId: job.requestId,
        items: job.request.items,
      });
    } else {
      // Transfer the STL buffers into the worker to avoid copying big blobs.
      const transfer = job.request.stls.map((s) => s.buffer);
      this.worker.postMessage(
        {
          type: "zip",
          requestId: job.requestId,
          filenames: job.request.filenames,
          stls: job.request.stls,
          bomText: job.request.bomText,
        },
        transfer,
      );
    }
  }

  // Request a mesh rebuild. `refit` reframes the camera; pass it only for the
  // first load and archetype switches. Coalesces while busy.
  requestMesh(refit = false) {
    // Busy if a mesh is in flight, an STL/zip job is running, or the worker
    // isn't up yet: queue a coalesced follow-up.
    if (!this.workerReady || this.meshInflight || this.activeJob) {
      this.dirty = true;
      // Sticky: don't let a plain param change clear a queued refit.
      this.dirtyRefit = this.dirtyRefit || refit;
      return;
    }
    this.meshInflight = true;
    this.cb.onMeshRequested();
    this.worker.postMessage({
      type: "mesh",
      requestId: ++this.requestId,
      scene: this.sceneJson(),
      refit,
    });
    // The request is on its way; clear the queued-refit latch.
    this.dirtyRefit = false;
  }

  // Build the printable STL files for a scene. Resolves with one named file per
  // exported solid (a standalone scene yields exactly one).
  requestStls(request: {
    scene: string;
    mode: string;
    clearance: number;
  }): Promise<SceneFile[]> {
    return new Promise((resolve, reject) => {
      this.jobs.push({
        kind: "stls",
        requestId: ++this.requestId,
        request,
        resolve,
        reject,
      });
      this.pump();
    });
  }

  // Build one 3MF package holding every cart item's solids (the mesh builds
  // run in the worker as part of this job). Resolves with the package bytes.
  request3mf(request: { items: ThreeMfItem[] }): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      this.jobs.push({
        kind: "3mf",
        requestId: ++this.requestId,
        request,
        resolve,
        reject,
      });
      this.pump();
    });
  }

  // Build a zip from named STL byte buffers plus a bom.txt. Resolves with the
  // zip bytes.
  requestZip(request: {
    filenames: string[];
    stls: Uint8Array[];
    bomText: string;
  }): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      this.jobs.push({
        kind: "zip",
        requestId: ++this.requestId,
        request,
        resolve,
        reject,
      });
      this.pump();
    });
  }
}
